import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, Input } from "antd";

import CrrDropDown from "./CrrDropdown/CrrDropdown";
import CrrTextarea from "./CrrTextarea/CrrTextarea";
import CrrDatePicker from "./CrrDatePicker/CrrDatePicker";
import CrrPassword from "./CrrPassword/CrrPassword";
import CrrBasicInput from "./CrrBasicInput/CrrBasicInput";
import CrrRadio from "./CrrRadio/CrrRadio";
import CrrMultiselect from "./CrrMultislect/CrrMultiselect";
import CrrDropDownWithExtApi from "./CrrDropDownWithExtApi/CrrDropDownWithExtApi";
import CrrGroupedMultiselect from "./CrrGroupedMultislect/CrrGroupedMultislect";
import CustomMultiSelect from "./CustomMultSelect/CustomMultiSelect";
import CrrNumberInput from "./CrrBasicInput/CrrNumberInput";
import images from "../../assets/images";

class NotNullError extends Error {
  constructor(message) {
    super(message);
    this.name = "NotNullError";
  }
}

const componentMap = {
  password: CrrPassword,
  date: CrrDatePicker,
  textarea: CrrTextarea,
  dropdown: CrrDropDown,
  ext_api_dropdown: CrrDropDownWithExtApi,
  multi_select: CrrMultiselect,
  multi_select_with_group: CrrGroupedMultiselect,
  dropdown_with_grouped_checkbox: CustomMultiSelect,
  number: CrrNumberInput,
  integer: CrrNumberInput,
};

function FormItem(props) {
  const {
    name,
    rules = [],
    placeholderText,
    inputType = "",
    className = "",
    disabled,
    defaultValue = "",
    expand = false,
    options = null,
    answer = null,
    multi_resp,
    setError,
    isLabelEnabled = true,
    formErrors = {},
    dependencies,
    customStyles,
    form,
  } = props;
  const [selectField, setselectField] = useState("");
  const [value, setValue] = useState(defaultValue);
  const [isFieldFocused, setIsFieldFocused] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [otherText, setOtherText] = useState(answer?.opted_answer_text || "");
  const otherTextRef = useRef(null); //to focus the text field when the tied value('other') is selected

  const formStates = {
    selectField,
    setselectField,
    value,
    setValue,
    isFieldFocused,
    setIsFieldFocused,
  };

  useEffect(() => {
    if (inputType === "dropdown" && !options) {
      throw new NotNullError("options can't be null");
    }
  }, []);
  const isNumberInput = inputType === "number" || inputType === "integer";

  const hasValue = multi_resp
    ? value && value.length > 0
    : value !== undefined && value !== null && value !== "";

  const hasDefaultValue = !!defaultValue;
  const hasAnswer = answer
    ? multi_resp
      ? answer.length > 0
      : answer !== null
    : false;

  useEffect(() => {
    if (
      isLabelEnabled &&
      (hasValue || // has a value in the field
        isFieldFocused || // is field focued
        ((hasDefaultValue || hasAnswer) && (!isMounted || hasValue))) // has a default answer in the initial load
    ) {
      setShowLabel(true);
      setIsMounted(true);
    } else {
      setShowLabel(false);
    }
  }, [isLabelEnabled, value, isFieldFocused, hasAnswer, hasDefaultValue]);

  const handleItemClick = () => {
    if (setError) {
      const { fieldError, setFieldError } = setError;
      const updatedErrors = fieldError.filter((error) => error !== name);
      setFieldError(updatedErrors);
    }
    !disabled && setselectField(true);
    setIsFieldFocused(true);
  };

  const validateSpaces = (_, value) => {
    if (Array.isArray(value) || typeof value === "object" || name === "email") {
      return Promise.resolve(); // Skip validation for arrays,objects and emails
    }
    if (value && typeof value === "string" && value.trim() === "") {
      return Promise.reject(new Error("Please fill the field to proceed!"));
    }
    return Promise.resolve();
  };

  const InputComponent = componentMap[inputType] || CrrBasicInput;
  const selectedOption = useMemo(
    () => options?.length > 0 && options.find((opt) => opt.value === value),
    [options, value]
  );

  const shouldShowTextField = selectedOption?.option_has_text;
  useEffect(() => {
    if (shouldShowTextField && answer?.opted_answer_text) {
      form?.setFieldsValue({
        [`${name}_other_text`]: answer.opted_answer_text,
      });
    }
  }, [shouldShowTextField, answer, name, form]);

  useEffect(() => {
    if (shouldShowTextField && otherTextRef.current && !otherText) {
      otherTextRef.current.focus();
    }
  }, [shouldShowTextField]);

  return (
    <>
      <Form.Item
        style={{ width: expand ? "100%" : "auto", ...customStyles }}
        disabled={disabled}
        name={name}
        rules={[...rules, { validator: validateSpaces }]}
        validateStatus={formErrors[name] ? "error" : ""}
        help={formErrors[name] ? formErrors[name] : null}
        dependencies={dependencies}
      >
        {inputType !== "radio_button" ? (
          <div
            tabIndex={-1}
            onClick={handleItemClick}
            onBlur={() => {
              setselectField(false);
              setIsFieldFocused(false);
            }}
            className={
              selectField
                ? ` ${className} selected-field`
                : ` ${className} field`
            }
          >
            {showLabel && (
              <label
                className="login-label"
                // Label stays dark whether resting or editing — focus is signalled
                // by the field's border + ring, not by turning the label blue.
                style={{ color: "var(--app-black-color)" }}
              >
                {placeholderText}
              </label>
            )}

            <InputComponent {...props} {...formStates} />
          </div>
        ) : (
          <CrrRadio {...props} {...formStates} />
        )}
      </Form.Item>
      {shouldShowTextField && (
        <Form.Item
          className="other_text"
          name={`${name}_other_text`}
          rules={[
            { required: true, message: "This field is required" },
            ,
            { validator: validateSpaces },
          ]}
        >
          <div className="dropdown-text-field">
            <img src={images.Connector} className="connector" />
            <div style={{ padding: "8px 12px 0px" }}>
              <Input
                className="checkbox-textfield
              preassessment-field
              field"
                placeholder={props?.other_placeholder}
                ref={otherTextRef}
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                onBlur={() => {
                  if (props.onChange && value && otherText.trim()) {
                    props.onChange({
                      question_id: props.qnId,
                      opted_answer_id: value,
                      opted_answer_text: otherText,
                    });
                  }
                }}
                defaultValue={answer?.opted_answer_text}
              />
            </div>
          </div>
        </Form.Item>
      )}
    </>
  );
}
export default FormItem;
